import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const PROXY_URL = 'https://lovense-proxy.estrobunny-xo.workers.dev';

export const LovensePanel = () => {
  const [connected, setConnected] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [testIntensity, setTestIntensity] = useState(10);
  const [connecting, setConnecting] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [userId] = useState(() => 'sp-' + Date.now());
  const [toys, setToys] = useState<any[]>([]);
  const [lastError, setLastError] = useState('');
  const [connectionInfo, setConnectionInfo] = useState<any>(null);

  const patterns = ['pulse', 'wave', 'fireworks', 'earthquake'];

  // Poll for connection status
  useEffect(() => {
    if (!connected && userId) {
      const interval = setInterval(async () => {
        try {
          const res = await fetch(`${PROXY_URL}/api/lovense/status?uid=${userId}`);
          const data = await res.json();
          setConnectionInfo(data);
          if (data.success && data.status === 'connected' && data.toys?.length > 0) {
            setToys(data.toys);
            setDeviceName(data.toys.map((t: any) => t.name).join(', '));
            setConnected(true);
            setQrCode('');
            toast.success('Connected!');
          }
        } catch {}
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [connected, userId]);

  const handleGetQRCode = async () => {
    setConnecting(true);
    setLastError('');
    try {
      const res = await fetch(`${PROXY_URL}/api/lovense/qr-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: userId, uname: 'SyncPlay' })
      });
      const data = await res.json();
      if (data.success && data.qr) {
        setQrCode(data.qr);
        toast.success('Scan this QR with Lovense Remote!');
      } else {
        setLastError(data.error || 'Failed');
        toast.error(data.error || 'Failed');
      }
    } catch {
      setLastError('Server unreachable');
      toast.error('Failed to connect to server');
    } finally {
      setConnecting(false);
    }
  };

  const sendCommand = async (command: string, action?: string, value?: number, extra?: any) => {
    try {
      const body: any = { uid: userId, command };
      if (action) body.action = action;
      if (value !== undefined) body.value = value;
      if (extra) Object.assign(body, extra);

      const res = await fetch(`${PROXY_URL}/api/lovense/command`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!data.success) {
        setLastError(`${data.error} (${data.method || 'unknown'})`);
        toast.error(data.error || 'Command failed');
      } else {
        setLastError('');
      }
    } catch {
      setLastError('Network error');
      toast.error('Failed to send command');
    }
  };

  const handleVibrate = (intensity: number) => {
    sendCommand('Function', `Vibrate:${Math.max(0, Math.min(20, intensity))}`);
  };

  const handlePreset = (name: string) => {
    sendCommand('Preset', undefined, undefined, { name, timeSec: 5 });
  };

  const handleStop = () => {
    sendCommand('Function', 'Stop');
  };

  const handleDisconnect = () => {
    setConnected(false);
    setQrCode('');
    setToys([]);
    setDeviceName('');
    setConnectionInfo(null);
    setLastError('');
  };

  return (
    <div className="card">
      <h3 className="card-title">Lovense Connection</h3>

      {/* Callback URL reminder */}
      {!connected && !qrCode && (
        <div style={{
          padding: '0.75rem',
          background: '#fff3cd',
          borderRadius: 'var(--border-radius)',
          marginBottom: '1rem',
          fontSize: '0.8rem',
          lineHeight: 1.5
        }}>
          <strong>Setup required:</strong> In your <a href="https://www.lovense.com/user/developer/info" target="_blank" rel="noopener noreferrer">Lovense Developer Dashboard</a>, set the Callback URL to:
          <br />
          <code style={{ wordBreak: 'break-all', fontSize: '0.75rem' }}>{PROXY_URL}/api/lovense/callback</code>
        </div>
      )}

      {!connected ? (
        <div>
          {!qrCode ? (
            <div>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem', fontSize: '0.9rem' }}>
                Generate a QR code, then scan it with the Lovense Remote app to connect your toy.
              </p>
              <button
                className="btn btn-primary"
                onClick={handleGetQRCode}
                disabled={connecting}
                style={{ width: '100%' }}
              >
                {connecting ? 'Generating...' : 'Generate QR Code'}
              </button>
            </div>
          ) : (
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                1. Open <strong>Lovense Remote</strong> app
              </p>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                2. Go to <strong>Discover → Scan</strong>
              </p>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem', fontSize: '0.9rem' }}>
                3. Scan this QR code
              </p>
              <img
                src={qrCode}
                alt="Scan with Lovense Remote"
                style={{
                  maxWidth: '250px',
                  width: '100%',
                  borderRadius: 'var(--border-radius)',
                  border: '2px solid var(--bg-tertiary)',
                  marginBottom: '1rem'
                }}
              />
              <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: '1rem' }}>
                Waiting for scan... Keep this app open.
              </p>
              <button
                className="btn btn-secondary"
                onClick={() => { setQrCode(''); setLastError(''); }}
                style={{ width: '100%' }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <span className="status-indicator">
              <span className="status-dot connected" />
              Connected
            </span>
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              {deviceName || 'Toy'}
            </span>
            <button
              className="btn btn-secondary"
              onClick={handleDisconnect}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', marginLeft: 'auto' }}
            >
              Disconnect
            </button>
          </div>

          {toys.length > 0 && (
            <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'var(--bg-tertiary)', borderRadius: 'var(--border-radius)' }}>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>Toys:</p>
              {toys.map((t) => (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
                  <span className={`status-dot ${t.connected ? 'connected' : ''}`} />
                  {t.name} {t.battery >= 0 && `(${t.battery}%)`}
                </div>
              ))}
            </div>
          )}

          <div style={{ marginBottom: '1rem' }}>
            <label className="form-label">Test Intensity</label>
            <input
              type="range"
              className="intensity-slider"
              min="0"
              max="20"
              value={testIntensity}
              onChange={(e) => setTestIntensity(parseInt(e.target.value))}
            />
            <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem' }}>{testIntensity}</p>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem' }}>
              <button className="btn btn-secondary" onClick={() => handleVibrate(testIntensity)}>
                Test Vibrate
              </button>
              <button className="btn btn-danger" onClick={handleStop}>
                Stop
              </button>
            </div>
          </div>

          <div>
            <label className="form-label">Test Patterns</label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {patterns.map((pattern) => (
                <button
                  key={pattern}
                  className="btn btn-secondary"
                  onClick={() => handlePreset(pattern)}
                >
                  {pattern}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Debug info */}
      <details style={{ marginTop: '1rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
        <summary style={{ cursor: 'pointer' }}>Debug info</summary>
        <div style={{ padding: '0.5rem', background: 'var(--bg-tertiary)', borderRadius: '4px', marginTop: '0.5rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
          UID: {userId}
          {'\n'}Status: {connectionInfo?.status || 'unknown'}
          {'\n'}Toys: {connectionInfo?.toys?.length || 0}
          {lastError && `\nError: ${lastError}`}
        </div>
      </details>
    </div>
  );
};
